import React from "react";
import { Link } from "react-router-dom";
import moment from "moment-timezone";

import { formatTimestampLong } from "utils/format";
import { useFeature } from "providers/OrganizationFeatures";
import { useRoutes } from "utils/routes";

import type { LogLineFieldsFragment } from "./gql/LogLineContent.v2.generated";

// A collapsed log line. Renders the LogLineFields fragment, which both the
// log-line list and the detail query use. The expanded view (full text +
// details) is LogLineDetails.

const CHIP = "bg-[#262626] rounded-[1px]";

function LogLineContent({
  logLine,
  serverId,
  databaseId: propDatabaseId,
}: {
  logLine: LogLineFieldsFragment;
  serverId: string;
  databaseId?: string;
}) {
  const occurredAt = moment(logLine.occurredAt);
  const {
    backendPid,
    postgresRoleName,
    postgresRoleId,
    logClassification,
    logText,
  } = logLine;
  const { serverRole, serverLogs, databaseLogs, databaseBackendAtTime } =
    useRoutes();
  const hasActivitySnapshotsFeature = useFeature("activitySnapshots");

  // For server-wide log lines fall back to the database ID we're on.
  const databaseId = logLine.databaseId ?? propDatabaseId;

  const classificationPath = databaseId
    ? databaseLogs(databaseId, logClassification)
    : serverLogs(serverId, logClassification);

  return (
    <span className="inline-block">
      {formatTimestampLong(occurredAt)}
      &nbsp;
      {logClassification && (
        <span>
          <Link
            className={`${CHIP} text-[#ba94da] hover:text-[#debdf9] focus:text-[#debdf9]`}
            to={classificationPath}
          >
            {logClassification}
          </Link>
          &nbsp;
        </span>
      )}
      {(!hasActivitySnapshotsFeature || !databaseId) && (
        <span className={`${CHIP} text-[#4ba64b]`}>{backendPid}</span>
      )}
      {hasActivitySnapshotsFeature && databaseId && (
        <Link
          className={`${CHIP} text-[#4ba64b] hover:text-[#7bd67b] focus:text-[#7bd67b]`}
          to={databaseBackendAtTime(databaseId, backendPid, occurredAt.unix())}
        >
          {backendPid}
        </Link>
      )}
      &nbsp;
      {postgresRoleName && (
        <span>
          <Link
            className={`${CHIP} text-[#6d9ec8] hover:text-[#9dcef8] focus:text-[#9dcef8]`}
            to={serverRole(serverId, postgresRoleId)}
          >
            {postgresRoleName}
          </Link>
          &nbsp;
        </span>
      )}
      {logText}
    </span>
  );
}

export default LogLineContent;
